import { Bot } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CanvasSessionPersistContext,
  Message,
} from '../../types/conversation';
import { MessageBubble } from './MessageBubble';

interface ConversationMessagesProps {
  messages: Message[];
  isStreaming: boolean;
  formatTime: (date: Date | string) => string;
  /** Quando definido, a última mensagem canvas persiste campos no servidor (execute-ai). */
  canvasSessionPersist?: CanvasSessionPersistContext | null;
}

export const ConversationMessages: React.FC<ConversationMessagesProps> = ({
  messages,
  isStreaming,
  formatTime,
  canvasSessionPersist = null,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming]);

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <Bot className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            {t('conversations.startConversation')}
          </p>
          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
            {t('conversations.typeMessageToStart')}
          </p>
        </div>
      </div>
    );
  }

  // Filtrar apenas mensagens do tipo 'user' e 'assistant', excluindo 'status'
  const filteredMessages = messages.filter(
    msg => msg.role !== 'status' && !msg.isStatus
  );

  const lastIndex = filteredMessages.length - 1;

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col gap-3">
      {filteredMessages.map((msg, index) => {
        const fillCanvasHeight =
          index === lastIndex &&
          msg.role === 'assistant' &&
          msg.useCanvas === true;
        const persistForMessage =
          canvasSessionPersist &&
          fillCanvasHeight &&
          msg.role === 'assistant' &&
          msg.useCanvas === true
            ? canvasSessionPersist
            : null;
        return (
          <div
            key={msg.id}
            className={`flex w-full min-w-0 ${
              msg.role === 'user' ? 'justify-end' : 'justify-start'
            } ${fillCanvasHeight ? 'min-h-0 flex-1 flex-col' : 'shrink-0'}`}
          >
            <MessageBubble
              message={msg}
              formatTime={formatTime}
              fillCanvasHeight={fillCanvasHeight}
              canvasSessionPersist={persistForMessage}
            />
          </div>
        );
      })}
      <div ref={messagesEndRef} className="h-0 w-full shrink-0" aria-hidden />
    </div>
  );
};
